import { FC } from 'react';
import {
  brandIcons,
  type BrandIconColor,
  type BrandIconName,
  type BrandIconSize,
} from './brand-icons';
import { SvgBrandIcon } from './styles';

interface BrandIconProps {
  name: BrandIconName;
  size?:
    | BrandIconSize
    | { base: BrandIconSize; tablet?: BrandIconSize; desktop?: BrandIconSize };
  color?: BrandIconColor;
  className?: string;
  'aria-label'?: string;
}

const BrandIcon: FC<BrandIconProps> = ({
  name,
  size = 'md',
  color,
  className,
  'aria-label': ariaLabel,
}) => {
  const def = brandIcons[name];
  const base = typeof size === 'string' ? size : size.base;
  const tablet = typeof size === 'string' ? undefined : size.tablet;
  const desktop = typeof size === 'string' ? undefined : size.desktop;

  return (
    <SvgBrandIcon
      viewBox={def.viewBox}
      fill={def.fill}
      $sizeBase={base}
      $sizeTablet={tablet}
      $sizeDesktop={desktop}
      $color={color}
      className={className}
      aria-hidden={ariaLabel ? undefined : true}
      aria-label={ariaLabel}
      role={ariaLabel ? 'img' : undefined}
      xmlns='http://www.w3.org/2000/svg'
    >
      {def.paths()}
    </SvgBrandIcon>
  );
};

export default BrandIcon;

/**
 * HOW TO IMPLEMENT - BRAND ICON
 * ---------------
 * Brand illustration icons (32px in design). Single-color via `currentColor` —
 * inherits the parent `color` by default, or set the `color` prop to pin it to
 * a design token (`dark` → `--actions-primary`, `light` → `--actions-inverse`).
 *
 * @example Basic usage — 32px, inherits parent color
 * <BrandIcon name="bloating" />
 *
 * @example Fixed size at all breakpoints
 * <BrandIcon name="gas" size="sm" />
 *
 * @example Fully responsive
 * <BrandIcon name="regularity" size={{ base: 'sm', tablet: 'md', desktop: 'md' }} />
 *
 * @example Token color — dark on light background
 * <BrandIcon name="digestive-health" color="dark" />
 *
 * @example Token color — light on dark background
 * <BrandIcon name="gut-barrier" color="light" />
 *
 * @example Accessible standalone icon
 * <BrandIcon name="2-week-relief" aria-label="2 week relief" />
 */
